import { Plural, Trans } from '@lingui/react'
import { isSameDay } from 'date-fns'
import React from 'react'
import styled from 'styled-components/macro'

import { useRoot } from '../../app/useRoot'
import { FCompanyBranchStatusFragment } from '../../graph/generated'
import { dateFormats, useFormatDate } from '../../helpers/formats'
import { useShopStatus } from '../../hooks/useShopStatus'

interface IProps {
  status: FCompanyBranchStatusFragment
  deliveryTime: number
  pickupTime: number
}

export const StyledStatusDisplayOpen = styled.span`
  font-style: italic;
  color: #0f9849;
`

export const StyledStatusDisplayClosed = styled.span`
  font-style: italic;
  color: #b40000;
`

export const ShopStatusDisplay: React.FC<IProps> = ({
  status,
  deliveryTime: branchDeliveryTime,
  pickupTime,
}) => {
  const { order, location } = useRoot()
  const formatDate = useFormatDate()
  const { decision, openingIn, closingIn } = useShopStatus({
    status,
  })

  if (!status) {
    return null
  }

  if (status.isOpen === false) {
    if (!status.willOpenAt) {
      return (
        <StyledStatusDisplayClosed>
          <Trans>We are closed until further notice</Trans>
        </StyledStatusDisplayClosed>
      )
    }
    const closestBH = status.businessHours[0]
    if (closestBH && closestBH.closedNote) {
      return (
        <StyledStatusDisplayClosed>
          {closestBH.closedNote}
        </StyledStatusDisplayClosed>
      )
    }
  }

  const formatOpenTime = (openAt: Date) => {
    const now = new Date()

    const format = isSameDay(openAt, now)
      ? dateFormats.hourMinute
      : dateFormats.weekdayHourMinute

    return formatDate(openAt, format)
  }

  // delivery time consists of base branch delivery time + delivery zone deviation (based on the order address)
  const deliveryTime = branchDeliveryTime + location.zoneDeliveryTimeDeviation

  switch (decision) {
    case 'Open':
      let timingDisplay
      if (order.usingDeliveryOrderMethod && status.defaultDeliveryTimeEnabled) {
        timingDisplay = (
          <Trans>We are delivering in {deliveryTime} minutes</Trans>
        )
      }
      if (order.usingPickupOrderMethod && status.defaultPickupTimeEnabled) {
        timingDisplay = (
          <Trans>We can prepare your order in {pickupTime} minutes</Trans>
        )
      }
      return (
        <StyledStatusDisplayOpen data-testid="statusDisplayOpen">
          {timingDisplay}
        </StyledStatusDisplayOpen>
      )
    case 'OpeningSoon':
      return (
        <StyledStatusDisplayClosed>
          <Plural
            value={openingIn}
            one="Opening in 1 minute"
            other="Opening in # minutes"
          />
        </StyledStatusDisplayClosed>
      )
    case 'ClosingSoon':
      return (
        <StyledStatusDisplayOpen>
          <Plural
            value={closingIn}
            one="Closing in 1 minute"
            other="Closing in # minutes"
          />
        </StyledStatusDisplayOpen>
      )
    case 'Closed':
      return (
        <StyledStatusDisplayClosed>
          {status.willOpenAt ? (
            <Trans>We will open at {formatOpenTime(status.willOpenAt)}</Trans>
          ) : (
            <Trans>Closed</Trans>
          )}
        </StyledStatusDisplayClosed>
      )
  }
}
